import { Alert, PageLoading } from '@edgebox/react-components';
import { FeatureSummary, SyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router';
import { siteRequests } from '../frame-messages';
import { isHostViewport } from './Customer/Governance/useHostViewport';
import { getUserFromJwt } from '../services/SyncCoreApiComponent';

export interface IAppParams {
  siteOptions: {
    jwt: string;
    syncCoreDomain: string;
    baseUrl: string;
    featureFlags: FeatureSummary;
  };
  pageOptions: any;
  /**
   * Set once the host reports where it has scrolled the frame (`host-viewport`): the host sizes the
   * frame to its content and scrolls it with its own page, so nothing in the frame scrolls.
   */
  hostScrollsFrame?: boolean;
}

export const AppParamsContext: any = React.createContext<IAppParams | null>(null);

interface IState extends Partial<IAppParams> {
  exchangeTokenError?: any;
}

interface IProps extends PropsWithChildren {}

export function AppParams(props: IProps) {
  const location = useLocation();

  const [siteOptions, setSiteOptions] = useState<IState['siteOptions']>(undefined);
  const [pageOptions, setPageOptions] = useState<IState['pageOptions']>(undefined);
  const [exchangeTokenError, setExchangeTokenError] = useState<IState['exchangeTokenError']>(undefined);
  const [hostScrollsFrame, setHostScrollsFrame] = useState(false);

  const onMessage = useCallback(
    async (message: any) => {
      if (message.type === 'options' || message.type === 'update-options') {
        setPageOptions(Array.isArray(message.options) ? {} : message.options);
      } else if (message.type === 'config') {
        const newSiteOptions = Array.isArray(message.config) ? {} : message.config;
        if (!newSiteOptions.jwt && newSiteOptions.backendJwt) {
          if (newSiteOptions.siteUuid) {
            const api = new SyncCoreApi({
              jwt: newSiteOptions.backendJwt,
              services: {
                syncCore: {
                  baseDomain: newSiteOptions.syncCoreDomain,
                },
              },
            });
            try {
              const { jwt } = await api.utility.backend.exchangeToken(newSiteOptions.siteUuid);
              newSiteOptions.jwt = jwt;
            } catch (e) {
              console.error('failed to exchange token', e);
              setExchangeTokenError(e);
              return;
            }
          } else {
            newSiteOptions.jwt = newSiteOptions.backendJwt;
          }
        }

        (window as any).LATEST_USER = getUserFromJwt(newSiteOptions.jwt) ?? getUserFromJwt(newSiteOptions.backendJwt);
        setSiteOptions(newSiteOptions);
      } else if (message.type === 'response') {
        if (siteRequests[message.callbackId]) {
          siteRequests[message.callbackId](message.response);
          delete siteRequests[message.callbackId];
        } else {
          console.error(`Entity list response for unknown callback id ${message.callbackId}.`, message);
        }
      } else {
        if (isHostViewport(message)) {
          setHostScrollsFrame(true);
        }
        window.dispatchEvent(new CustomEvent('content-sync-parent-message', { detail: message }));
      }
    },
    [setPageOptions, setSiteOptions, setExchangeTokenError, setHostScrollsFrame]
  );

  useEffect(() => {
    const broker = (window as any).IFRAME_MESSAGE_BROKER;

    broker.onMessage = onMessage;
  }, [onMessage]);

  useEffect(() => {
    const broker = (window as any).IFRAME_MESSAGE_BROKER;

    // A host that scrolls the frame reports its position on every scroll; only the latest report is
    // worth replaying, so the older ones leave the queue here.
    const queued: unknown[] = broker.messages;
    const latestHostViewport = queued.reduce<number>((latest, message, index) => (isHostViewport(message) ? index : latest), -1);
    broker.messages = queued.filter((message, index) => !isHostViewport(message) || index === latestHostViewport);

    if (broker.messages.length) {
      broker.messages.forEach(broker.onMessage);
    }

    document.body.classList.remove('size-line', 'size-box', 'size-page');
    for (const className of Array.from(document.body.classList)) {
      if (className.startsWith('page-')) {
        document.body.classList.remove(className);
      }
    }

    // Apply classes to the <body> that allow styling based on the iframe
    // size for a box, a line a page.
    const isLine = location.pathname.substring(0, 18) === '/box/update-status';
    const isBox = location.pathname.substring(0, 5) === '/box/';
    if (isLine) {
      document.body.classList.add('size-line');
    } else if (isBox) {
      document.body.classList.add('size-box');
    } else {
      document.body.classList.add('size-page');
    }

    for (const path of location.pathname.substring(1).split('/')) {
      document.body.classList.add('page-' + path.replace(/[^a-z0-9-]/g, '-'));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (pageOptions?.brandPresencePageBackground) {
      document.body.classList.add('brand-presence-page-background');
    } else {
      document.body.classList.remove('brand-presence-page-background');
    }
  }, [pageOptions?.brandPresencePageBackground]);

  // A host whose frame has a fixed height keeps the body the frame's scroll container; every other
  // host sizes the frame to the page's content.
  useEffect(() => {
    if (pageOptions?.brandPresenceAllowBodyScroll) {
      document.body.classList.add('brand-presence-fixed-frame');
    } else {
      document.body.classList.remove('brand-presence-fixed-frame');
    }
  }, [pageOptions?.brandPresenceAllowBodyScroll]);

  const small = location.pathname.substring(0, 5) === '/box/';

  const state: IState = useMemo(
    () => ({
      siteOptions,
      pageOptions,
      hostScrollsFrame,
      exchangeTokenError,
    }),
    [siteOptions, pageOptions, hostScrollsFrame, exchangeTokenError]
  );

  return (
    <AppParamsContext.Provider value={state}>
      {state?.siteOptions && state?.pageOptions ? (
        props.children
      ) : state?.exchangeTokenError ? (
        small ? (
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" title={'Failed to exchange auth token with Sync Core.'} />
        ) : (
          <Alert variant="warning">Failed to exchange auth token with Sync Core.</Alert>
        )
      ) : small ? (
        <div className="text-center">
          <FontAwesomeIcon
            icon={faSpinner}
            spin={true}
            //size={"4x"}
            className={'text-secondary'}
            style={{ margin: '1rem 0 0 0' }}
          />
        </div>
      ) : (
        <PageLoading>Loading...</PageLoading>
      )}
    </AppParamsContext.Provider>
  );
}
